import { TRIAL_MOCK_IDS } from '../constants';

// Single generated dataset shared by every trial-mode mock resolver, so
// branch totals, group member counts, dashboard cards and the transactions
// list always agree with each other. Dates are computed relative to `NOW`
// (captured once at module load) instead of hardcoded, so demo data always
// falls within the trailing 3 months.

const BRANCH_CONFIGS = [
  {
    id: TRIAL_MOCK_IDS.BRANCH_ID,
    name: TRIAL_MOCK_IDS.BRANCH_NAME,
    studentCount: 50,
    groupCount: 12,
    addressLine: '12, MG Road',
    city: 'Bengaluru',
    state: 'Karnataka',
    pincode: '560001'
  },
  {
    id: 'trial-branch-002',
    name: 'North Branch',
    studentCount: 20,
    groupCount: 3,
    addressLine: '45, Connaught Place',
    city: 'New Delhi',
    state: 'Delhi',
    pincode: '110001'
  },
  {
    id: 'trial-branch-003',
    name: 'South Branch',
    studentCount: 15,
    groupCount: 1,
    addressLine: '8, Anna Salai',
    city: 'Chennai',
    state: 'Tamil Nadu',
    pincode: '600001'
  }
] as const;

const ACTIVITY_NAMES = [
  'Yoga Morning Batch',
  'Swimming Advanced',
  'Dance Beginners',
  'Karate Kids',
  'Music Theory',
  'Gym Membership',
  'Art Workshop',
  'Cricket Coaching',
  'Piano Classes',
  'Badminton Club',
  'Chess Club',
  'Coding Bootcamp',
  'Football Training',
  'Basketball Club',
  'Guitar Lessons',
  'Pottery Class'
];

const FEE_AMOUNTS = [800, 1000, 1200, 1500, 1800, 2000, 2200, 2500];

export const FIRST_NAMES = [
  'Aarav',
  'Priya',
  'Rohan',
  'Meera',
  'Arjun',
  'Ananya',
  'Vikram',
  'Sneha',
  'Karthik',
  'Divya',
  'Rahul',
  'Ishita',
  'Aditya',
  'Nisha',
  'Suresh'
];

export const LAST_NAMES = [
  'Sharma',
  'Patel',
  'Nair',
  'Reddy',
  'Kumar',
  'Gupta',
  'Menon',
  'Iyer',
  'Singh',
  'Das',
  'Joshi',
  'Verma',
  'Rao',
  'Pillai',
  'Mishra'
];

const NOW = new Date();

export const daysAgo = (d: number) => {
  const dt = new Date(NOW);
  dt.setDate(dt.getDate() - d);
  return dt.toISOString();
};

const monthsFromNow = (m: number) => {
  const dt = new Date(NOW);
  dt.setMonth(dt.getMonth() + m);
  return dt.toISOString();
};

// ── Groups ──

const ALL_GROUPS = BRANCH_CONFIGS.flatMap((branch, branchIndex) => {
  const startIndex = BRANCH_CONFIGS.slice(0, branchIndex).reduce(
    (sum, b) => sum + b.groupCount,
    0
  );
  return Array.from({ length: branch.groupCount }, (_, g) => {
    const globalIndex = startIndex + g;
    return {
      id: `${branch.id}-grp-${String(g + 1).padStart(2, '0')}`,
      name: ACTIVITY_NAMES[globalIndex % ACTIVITY_NAMES.length],
      branch_id: branch.id,
      branch_name: branch.name,
      amount: String(FEE_AMOUNTS[globalIndex % FEE_AMOUNTS.length]),
      interval: 'monthly',
      intervalFrequency: 1,
      is_active: true,
      is_payment_link_sent: globalIndex % 2 === 0,
      is_split_payment: false,
      member_count: 0,
      active_member_count: 0,
      pay_due: '0',
      pay_received: '0',
      pending_count: 0,
      image_url: '',
      collection_started_at: monthsFromNow(-3),
      next_due_at: monthsFromNow(1)
    };
  });
});

type GeneratedGroup = (typeof ALL_GROUPS)[number];

// ── Members ──

const memberGroupAssignments = new Map<string, GeneratedGroup[]>();

const ALL_MEMBERS = BRANCH_CONFIGS.flatMap((branch) => {
  const branchGroups = ALL_GROUPS.filter((g) => g.branch_id === branch.id);

  return Array.from({ length: branch.studentCount }, (_, i) => {
    const first = FIRST_NAMES[i % FIRST_NAMES.length];
    const last =
      LAST_NAMES[Math.floor(i / FIRST_NAMES.length) % LAST_NAMES.length];
    const id = `${branch.id}-member-${String(i + 1).padStart(3, '0')}`;

    const primaryGroup = branchGroups[i % branchGroups.length];
    const assignedGroups = [primaryGroup];
    if (branchGroups.length > 1 && i % 3 === 2) {
      assignedGroups.push(branchGroups[(i + 1) % branchGroups.length]);
    }
    memberGroupAssignments.set(id, assignedGroups);

    return {
      id,
      member_id: id,
      branch_id: branch.id,
      branch_name: branch.name,
      name: `${first} ${last}`,
      country_code: '91',
      mobile: `98${String(70000000 + i * 1111111).slice(0, 8)}`,
      primary_country_code: '91',
      primary_mobile_number: `98${String(70000000 + i * 1111111).slice(0, 8)}`,
      groups: assignedGroups.map((g) => ({
        id: g.id,
        name: g.name,
        is_active: g.is_active
      })),
      guardian_name:
        i % 3 === 0 ? `${LAST_NAMES[(i + 5) % LAST_NAMES.length]} Sr.` : '',
      admission_number: `ACM-${branch.id.slice(-3)}${String(i + 1).padStart(
        3,
        '0'
      )}`,
      admission_date: daysAgo(180 + i * 3),
      dob: daysAgo(365 * (8 + (i % 10))),
      email: `${first.toLowerCase()}.${last.toLowerCase()}@example.com`,
      secondary_country_code: '91',
      secondary_mobile_number: `97${String(60000000 + i * 1111113).slice(
        0,
        8
      )}`,
      address_line_1: `Flat ${100 + i}, ${branch.addressLine}`,
      address_line_2: '',
      city: branch.city,
      state: branch.state,
      country: 'India',
      pincode: branch.pincode,
      is_active: true,
      total_due: undefined as string | undefined,
      total_collected: '0',
      processing: [] as {
        amount: string;
        paid_on: string;
        payment_order_id: string;
      }[]
    };
  });
});

// ── Transactions ──

const PAYMENT_METHODS = ['upi', 'cards', 'net_banking', 'offline'];

const resolveTxOutcome = (monthOffset: number, variety: number) => {
  const paymentMethod = PAYMENT_METHODS[variety % PAYMENT_METHODS.length];

  if (monthOffset === 2) {
    return {
      status: 'success',
      settlementStatus: 'settled',
      paymentMode: 'automatic',
      paymentMethod,
      isSplitPayment: false,
      paidRatio: 1
    };
  }

  if (monthOffset === 1) {
    if (variety === 4) {
      return {
        status: 'processing',
        settlementStatus: '',
        paymentMode: '',
        paymentMethod: '',
        isSplitPayment: false,
        paidRatio: 0
      };
    }
    if (variety === 3) {
      return {
        status: 'processing',
        settlementStatus: '',
        paymentMode: 'automatic',
        paymentMethod,
        isSplitPayment: true,
        paidRatio: 0.6
      };
    }
    return {
      status: 'success',
      settlementStatus: 'settled',
      paymentMode: 'automatic',
      paymentMethod,
      isSplitPayment: false,
      paidRatio: 1
    };
  }

  // monthOffset === 0 (current month, most fees still outstanding)
  if (variety <= 1) {
    return {
      status: 'success',
      settlementStatus: 'settled',
      paymentMode: 'automatic',
      paymentMethod,
      isSplitPayment: false,
      paidRatio: 1
    };
  }
  return {
    status: 'processing',
    settlementStatus: '',
    paymentMode: '',
    paymentMethod: '',
    isSplitPayment: false,
    paidRatio: 0
  };
};

const ALL_TRANSACTIONS = ALL_MEMBERS.flatMap((member, mi) => {
  const assignedGroups = memberGroupAssignments.get(member.id) || [];

  return assignedGroups.flatMap((group, gi) =>
    [2, 1, 0].map((monthOffset) => {
      const dayInMonth = (mi * 7 + gi * 3) % 25;
      const baseDays = monthOffset * 30 + dayInMonth;
      const dueDays = Math.min(baseDays + 5, 89);
      const variety = (mi + gi) % 5;
      const outcome = resolveTxOutcome(monthOffset, variety);

      const paidAmount =
        outcome.paidRatio > 0
          ? Math.round(Number(group.amount) * outcome.paidRatio)
          : 0;
      const paidDays = Math.max(dueDays - 5, 0);
      const settledDays = Math.max(dueDays - 8, 0);

      return {
        id: `trial-ord-${group.branch_id.slice(-3)}-${String(mi + 1).padStart(
          3,
          '0'
        )}-${gi}-${monthOffset}`,
        branch_id: group.branch_id,
        branch_name: group.branch_name,
        remind_again: false,
        payment_link: 'https://pay.cofee.life/trial',
        type: 'group',
        order_short_url: '',
        receipt_url: '',
        payment_description: '',
        payment_to: TRIAL_MOCK_IDS.ORG_NAME,
        disabled_at: null,
        name: member.name,
        member_id: member.id,
        member_phone_number: `+${member.country_code}${member.mobile}`,
        note: `Monthly fee - ${group.name}`,
        amount: group.amount,
        total_amount: group.amount,
        original_amount_requested: group.amount,
        original_total_amount_requested: group.amount,
        status: outcome.status,
        settlement_status: outcome.settlementStatus,
        payment_mode: outcome.paymentMode,
        payment_method: outcome.paymentMethod,
        paid_at: outcome.paidRatio > 0 ? daysAgo(paidDays) : null,
        due_at: daysAgo(dueDays),
        settled_at:
          outcome.settlementStatus === 'settled' ? daysAgo(settledDays) : '',
        total_settled_amount:
          outcome.settlementStatus === 'settled' ? String(paidAmount) : '0',
        total_paid_amount: String(paidAmount),
        is_split_payment: outcome.isSplitPayment,
        utr:
          outcome.paidRatio > 0
            ? `UTR${String(
                900000000 + (mi * 13 + gi * 7 + monthOffset)
              ).padStart(12, '0')}`
            : '',
        group_id: group.id,
        group_name: group.name
      };
    })
  );
});

// Keep group/member aggregates in sync with the actual transactions above,
// instead of separately hardcoded (and easily stale) numbers.
ALL_GROUPS.forEach((group) => {
  const groupMembers = ALL_MEMBERS.filter((m) =>
    m.groups.some((g) => g.id === group.id)
  );
  group.member_count = groupMembers.length;
  group.active_member_count = groupMembers.filter((m) => m.is_active).length;

  const groupTransactions = ALL_TRANSACTIONS.filter(
    (t) => t.group_id === group.id
  );
  group.pay_received = String(
    groupTransactions.reduce((sum, t) => sum + Number(t.total_paid_amount), 0)
  );
  group.pay_due = String(
    groupTransactions
      .filter((t) => t.status === 'processing')
      .reduce(
        (sum, t) => sum + (Number(t.amount) - Number(t.total_paid_amount)),
        0
      )
  );
  group.pending_count = groupTransactions.filter(
    (t) => t.status === 'processing'
  ).length;
});

ALL_MEMBERS.forEach((member) => {
  const memberTransactions = ALL_TRANSACTIONS.filter(
    (t) => t.member_id === member.id
  );

  let dueSum = 0;
  let collectedSum = 0;
  const processing: typeof member.processing = [];

  memberTransactions.forEach((t) => {
    const amount = Number(t.amount) || 0;
    const paid = Number(t.total_paid_amount) || 0;
    collectedSum += paid;

    if (t.status === 'processing') {
      const remainingDue = amount - paid;
      dueSum += remainingDue;
      processing.push({
        amount: String(remainingDue),
        paid_on: t.due_at,
        payment_order_id: t.id
      });
    }
  });

  member.total_due = dueSum > 0 ? String(dueSum) : undefined;
  member.total_collected = String(collectedSum);
  member.processing = processing;
});

// ── Branches (aggregated from the groups/members/transactions above) ──

const ALL_BRANCHES = BRANCH_CONFIGS.map((branch) => {
  const branchGroups = ALL_GROUPS.filter((g) => g.branch_id === branch.id);
  const branchMembers = ALL_MEMBERS.filter((m) => m.branch_id === branch.id);
  const branchTransactions = ALL_TRANSACTIONS.filter(
    (t) => t.branch_id === branch.id
  );

  return {
    id: branch.id,
    name: branch.name,
    organisation_id: TRIAL_MOCK_IDS.ORGANIZATION_ID,
    status: 'active',
    is_default: branch.id === TRIAL_MOCK_IDS.BRANCH_ID,
    total_groups: branchGroups.length,
    total_members: branchMembers.length,
    currency: {
      code: 'INR',
      symbol: '₹',
      name: 'Indian Rupee'
    },
    fee_categories: [],
    payment_stats: {
      total_collected: branchTransactions.reduce(
        (sum, t) => sum + Number(t.total_paid_amount),
        0
      ),
      total_due: branchTransactions
        .filter((t) => t.status === 'processing')
        .reduce(
          (sum, t) => sum + (Number(t.amount) - Number(t.total_paid_amount)),
          0
        )
    },
    address_line_1: branch.addressLine,
    address_line_2: '',
    city: branch.city,
    state: branch.state,
    country: 'India',
    pincode: branch.pincode,
    is_fee_configured: false,
    branch_config: {}
  };
});

export {
  ALL_BRANCHES,
  ALL_GROUPS,
  ALL_MEMBERS,
  ALL_TRANSACTIONS,
  memberGroupAssignments
};

export const getGroupsForBranch = (branchId?: string) =>
  branchId ? ALL_GROUPS.filter((g) => g.branch_id === branchId) : ALL_GROUPS;

export const getMembersForBranch = (branchId?: string) =>
  branchId ? ALL_MEMBERS.filter((m) => m.branch_id === branchId) : ALL_MEMBERS;

export const getTransactionsForBranch = (branchId?: string) =>
  branchId
    ? ALL_TRANSACTIONS.filter((t) => t.branch_id === branchId)
    : ALL_TRANSACTIONS;

export const filterTransactionsInRange = (
  branchId: string | undefined,
  start: Date,
  end: Date,
  dateField: 'due_at' | 'paid_at' = 'due_at'
) =>
  getTransactionsForBranch(branchId).filter((t) => {
    const raw = t[dateField];
    if (!raw) return false;
    const d = new Date(raw);
    return d >= start && d <= end;
  });

export const getOrgAggregate = () => {
  const totalCollected = ALL_TRANSACTIONS.reduce(
    (sum, t) => sum + Number(t.total_paid_amount),
    0
  );
  const totalDue = ALL_TRANSACTIONS.filter(
    (t) => t.status === 'processing'
  ).reduce(
    (sum, t) => sum + (Number(t.amount) - Number(t.total_paid_amount)),
    0
  );

  return {
    totalGroups: ALL_GROUPS.length,
    totalMembers: ALL_MEMBERS.length,
    totalCollected,
    totalDue
  };
};
